import { useState, useEffect, useRef } from "react";
import {
  Shield, Activity, Database, Lock, ArrowRight, ArrowLeft
} from "lucide-react";
import { openConsultationModal } from "./ConsultationModal";

const ENGINES = [
  {
    id: "01",
    num: "01",
    title: "Threat Containment",
    sub: "Detect · Isolate · Neutralize",
    theme: "orange",
    color: "#FF5A1F",
    lightBg: "bg-orange-50",
    borderActive: "border-[#FF5A1F]",
    glow: "rgba(255, 90, 31, 0.35)",
    icon: Shield,
    iconBg: "bg-orange-50 border-orange-200/80 text-[#FF5A1F]",
    pos: "top-left",
    cardClass: "top-[5%] left-[2%] xl:left-[4%]",
    node: { x: 260, y: 155 },
    desc: "Kernel-level eBPF monitors detect webshells, unauthorized daemon forks, and reverse shells, instantly quarantining malicious processes in sub-2 seconds.",
    telemetry: {
      stat1: "03 Detected",
      stat2: "02 Isolated",
      stat3: "100% Safe"
    }
  },
  {
    id: "02",
    num: "02",
    title: "Autonomous SRE",
    sub: "Monitor · Heal · Optimize",
    theme: "amber",
    color: "#F59E0B",
    lightBg: "bg-amber-50",
    borderActive: "border-[#F59E0B]",
    glow: "rgba(245, 158, 11, 0.35)",
    icon: Activity,
    iconBg: "bg-amber-50 border-amber-200/80 text-amber-500",
    pos: "top-right",
    cardClass: "top-[5%] right-[2%] xl:right-[4%]",
    node: { x: 740, y: 155 },
    desc: "Self-healing daemon restarts failing services, purges runaway temp storage, and resolves CPU/RAM saturation before downtime strikes.",
    telemetry: {
      stat1: "98% Healthy",
      stat2: "2 Auto-Healed",
      stat3: "<1s Response"
    }
  },
  {
    id: "03",
    num: "03",
    title: "Data Resilience",
    sub: "Backup · Recover · Verify",
    theme: "blue",
    color: "#0284C7",
    lightBg: "bg-sky-50",
    borderActive: "border-[#0284C7]",
    glow: "rgba(2, 132, 199, 0.35)",
    icon: Database,
    iconBg: "bg-sky-50 border-sky-100 text-sky-500",
    pos: "bottom-right",
    cardClass: "bottom-[12%] right-[2%] xl:right-[4%]",
    node: { x: 740, y: 345 },
    desc: "Automated incremental snapshots with cryptographic integrity verification, instant point-in-time recovery, and multi-region replication.",
    telemetry: {
      stat1: "3 Replicas",
      stat2: "100% Integrity",
      stat3: "<30s Recovery"
    }
  },
  {
    id: "04",
    num: "04",
    title: "Governed Fleet",
    sub: "Control · Secure · Scale",
    theme: "emerald",
    color: "#10B981",
    lightBg: "bg-emerald-50",
    borderActive: "border-[#10B981]",
    glow: "rgba(16, 185, 129, 0.35)",
    icon: Lock,
    iconBg: "bg-emerald-50 border-emerald-100 text-emerald-500",
    pos: "bottom-left",
    cardClass: "bottom-[12%] left-[2%] xl:left-[4%]",
    node: { x: 260, y: 345 },
    desc: "Centralized configuration enforcement, signed daemon updates, granular role-based access control, and comprehensive DPDP audit logs across all nodes.",
    telemetry: {
      stat1: "98% Compliant",
      stat2: "24 Servers",
      stat3: "48 Endpoints"
    }
  }
];

const COLORS_RGB = [
  [255, 90, 31],   // 01 #FF5A1F (Vibrant Orange)
  [245, 158, 11],  // 02 #F59E0B (Solar Gold / Amber)
  [2, 132, 199],   // 03 #0284C7 (Cyber Sky Blue)
  [16, 185, 129]   // 04 #10B981 (Sovereign Emerald)
];

const STEP_DURATION_MS = 4000;
const ELLIPSE_CX = 500;
const ELLIPSE_CY = 250;
const ELLIPSE_RX = 340;
const ELLIPSE_RY = 135;

// Continuous mathematical weight function for Step k given progress p in [0, 4)
function getStepWeight(p, k) {
  let diff = p - k;
  while (diff > 2) diff -= 4;
  while (diff < -2) diff += 4;
  if (diff >= -0.5 && diff < 0) {
    return (diff + 0.5) / 0.5;
  }
  if (diff >= 0 && diff <= 0.5) {
    return 1.0;
  }
  if (diff > 0.5 && diff <= 1.0) {
    return 1.0 - (diff - 0.5) / 0.5;
  }
  return 0.0;
}

// Seamless RGB color morph based on step weights
function getBlendedColor(weights) {
  let r = 0, g = 0, b = 0;
  for (let i = 0; i < 4; i++) {
    r += COLORS_RGB[i][0] * weights[i];
    g += COLORS_RGB[i][1] * weights[i];
    b += COLORS_RGB[i][2] * weights[i];
  }
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
}

// Convert angle in degrees to Cartesian point on the radar ellipse
function getEllipsePt(deg) {
  const rad = (deg * Math.PI) / 180;
  return {
    x: ELLIPSE_CX + ELLIPSE_RX * Math.cos(rad),
    y: ELLIPSE_CY + ELLIPSE_RY * Math.sin(rad)
  };
}

// ── PURE 3D ISOMETRIC ANIMATED SVG CORE (DYNAMIC COLOR MORPHING FOR ALL 4 STEPS) ──
const Engine3DIsometricCore = ({ weights, blendedColor, progress }) => {
  // Planetary orbital ring tilted around sphere (rx=76, ry=22, rot=-18 deg)
  const beadAngle = (progress * 360 * 1.5) % 360;
  const beadRad = (beadAngle * Math.PI) / 180;
  const beadX0 = 76 * Math.cos(beadRad);
  const beadY0 = 22 * Math.sin(beadRad);
  const isBeadFront = beadY0 >= 0;

  return (
    <svg
      viewBox="0 0 340 300"
      className="w-full h-full overflow-visible select-none pointer-events-none"
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        {/* Soft shadow & glow filters */}
        <filter id="coreFilter" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="3.2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        <filter id="intenseGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* Metallic pedestal rim gradient */}
        <linearGradient id="pedRimGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#CBD5E1" />
          <stop offset="35%" stopColor="#F8FAFC" />
          <stop offset="70%" stopColor="#E2E8F0" />
          <stop offset="100%" stopColor="#94A3B8" />
        </linearGradient>

        {/* Pedestal top deck gradient */}
        <linearGradient id="pedTopGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E2E8F0" />
        </linearGradient>

        {/* 3D Glass Sphere Volume Gradient (dynamically tinted with blendedColor) */}
        <radialGradient id="glassSphereGrad" cx="35%" cy="30%" r="68%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.45" />
          <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.12" />
          <stop offset="75%" stopColor={blendedColor} stopOpacity="0.16" />
          <stop offset="96%" stopColor="#FFFFFF" stopOpacity="0.75" />
          <stop offset="100%" stopColor={blendedColor} stopOpacity="0.5" />
        </radialGradient>

        {/* Internal ambient sphere glow */}
        <radialGradient id="sphereInternalGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={blendedColor} stopOpacity="0.5" />
          <stop offset="65%" stopColor={blendedColor} stopOpacity="0.2" />
          <stop offset="100%" stopColor={blendedColor} stopOpacity="0" />
        </radialGradient>

        {/* ── STEP 1: ORANGE SHIELD GRADIENTS ── */}
        <linearGradient id="shield3DGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FF7A3D" />
          <stop offset="60%" stopColor="#EA580C" />
          <stop offset="100%" stopColor="#9A3412" />
        </linearGradient>

        {/* ── STEP 2: AMBER/GOLD ECG GRADIENTS ── */}
        <linearGradient id="ecgWaveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#F59E0B" />
          <stop offset="50%" stopColor="#FBBF24" />
          <stop offset="100%" stopColor="#D97706" />
        </linearGradient>

        {/* ── STEP 3: CYBER BLUE DATABASE GRADIENTS ── */}
        <linearGradient id="db3DTopGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#38BDF8" />
          <stop offset="50%" stopColor="#7DD3FC" />
          <stop offset="100%" stopColor="#0284C7" />
        </linearGradient>
        <linearGradient id="db3DSideGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0284C7" />
          <stop offset="100%" stopColor="#075985" />
        </linearGradient>

        {/* ── STEP 4: EMERALD GREEN PADLOCK GRADIENTS ── */}
        <linearGradient id="lock3DGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#34D399" />
          <stop offset="60%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#065F46" />
        </linearGradient>
      </defs>

      {/* ── 1. GROUND DROP SHADOW ────────────────────────────────────────────── */}
      <ellipse
        cx="170"
        cy="228"
        rx="68"
        ry="18"
        fill="rgba(15, 23, 42, 0.12)"
        filter="url(#coreFilter)"
      />

      {/* ── 2. METALLIC CIRCULAR BASE PEDESTAL ───────────────────────────────── */}
      {/* Outer Cylinder Wall */}
      <path
        d="M 116,216 A 54 15 0 0 0 224,216 L 224,224 A 54 15 0 0 1 116,224 Z"
        fill="url(#pedRimGrad)"
      />
      {/* Top Deck Surface */}
      <ellipse
        cx="170"
        cy="216"
        rx="54"
        ry="15"
        fill="url(#pedTopGrad)"
        stroke="#E2E8F0"
        strokeWidth="1"
      />

      {/* Inner Recessed Glowing Neon LED Ring (Color Matches Step) */}
      <ellipse
        cx="170"
        cy="214"
        rx="44"
        ry="12"
        fill="none"
        stroke={blendedColor}
        strokeWidth="3.4"
        filter="url(#coreFilter)"
      />
      <ellipse
        cx="170"
        cy="214"
        rx="44"
        ry="12"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="1.2"
        opacity="0.95"
      />

      {/* Upward Radiant Pedestal Illumination Spotlight (Color Matches Step) */}
      <ellipse
        cx="170"
        cy="208"
        rx="38"
        ry="11"
        fill={blendedColor}
        opacity="0.55"
        filter="url(#intenseGlow)"
      />

      {/* ── 3. TILTED PLANETARY ORBITAL RING (BACK HALF - BEHIND SPHERE) ─────── */}
      <g transform="translate(170, 154) rotate(-18)">
        <path
          d="M 76,0 A 76 22 0 0 0 -76,0"
          stroke={blendedColor}
          strokeWidth="1.5"
          fill="none"
          opacity="0.45"
        />
        {/* Orbiting Satellite Bead (When behind sphere) */}
        {!isBeadFront && (
          <g transform={`translate(${beadX0}, ${beadY0})`}>
            <circle r="4.2" fill={blendedColor} filter="url(#coreFilter)" opacity="0.6" />
            <circle r="2.2" fill="#FFFFFF" opacity="0.8" />
          </g>
        )}
      </g>

      {/* ── 4. INTERNAL VOLUMETRIC SPHERE GLOW (Color Matches Step) ─────────── */}
      <circle
        cx="170"
        cy="154"
        r="54"
        fill="url(#sphereInternalGlow)"
      />

      {/* ── 5. THE 4 3D HOLOGRAPHIC SYMBOLS (DISTINCT VIBRANT THEME COLORS) ──── */}

      {/* ── SLIDER 01: 3D SOVEREIGN SHIELD (VIBRANT GLOWING ORANGE) ───────────── */}
      <g opacity={weights[0]} className="transition-opacity duration-75">
        {/* Outer Shield Body */}
        <path
          d="M 170,118 L 198,128 L 198,154 Q 198,176 170,188 Q 142,176 142,154 L 142,128 Z"
          fill="url(#shield3DGrad)"
          stroke="#FFD5C0"
          strokeWidth="1.8"
          filter="url(#coreFilter)"
        />
        {/* Inner Shield Bevel Face */}
        <path
          d="M 170,124 L 192,132 L 192,152 Q 192,170 170,181 Q 148,170 148,152 L 148,132 Z"
          fill="#C2410C"
          opacity="0.88"
        />
        <path
          d="M 170,124 L 192,132 L 192,152 Q 192,170 170,181 Z"
          fill="#EA580C"
        />

        {/* Center Target Crosshair System */}
        <circle
          cx="170"
          cy="154"
          r="10.5"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.4"
          filter="url(#coreFilter)"
        />
        <circle
          cx="170"
          cy="154"
          r="3.8"
          fill="#FFFFFF"
        />
        {/* 4 Crosshair Tick Marks */}
        <line x1="170" y1="139" x2="170" y2="145" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
        <line x1="170" y1="163" x2="170" y2="169" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
        <line x1="155" y1="154" x2="161" y2="154" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />
        <line x1="179" y1="154" x2="185" y2="154" stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" />

        {/* Specular Star Glint on Shield */}
        <path
          d="M 158,124 Q 158,130 164,130 Q 158,130 158,136 Q 158,130 152,130 Q 158,130 158,124 Z"
          fill="#FFFFFF"
          filter="url(#coreFilter)"
        />
      </g>

      {/* ── SLIDER 02: 3D AUTONOMOUS SRE (GLOWING SOLAR GOLD / AMBER) ────────── */}
      <g opacity={weights[1]} className="transition-opacity duration-75">
        {/* Ambient Waveform Amber Glow */}
        <path
          d="M 132,154 L 146,154 L 152,166 L 162,118 L 174,186 L 182,140 L 188,162 L 194,154 L 208,154"
          fill="none"
          stroke="#F59E0B"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.7"
          filter="url(#coreFilter)"
        />
        {/* Amber Middle Waveform */}
        <path
          d="M 132,154 L 146,154 L 152,166 L 162,118 L 174,186 L 182,140 L 188,162 L 194,154 L 208,154"
          fill="none"
          stroke="url(#ecgWaveGrad)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Sharp White Neon Core Waveform */}
        <path
          d="M 132,154 L 146,154 L 152,166 L 162,118 L 174,186 L 182,140 L 188,162 L 194,154 L 208,154"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Peak & Trough Traveling Highlight Beads */}
        <circle
          cx="162"
          cy="118"
          r="4"
          fill="#FFFFFF"
          filter="url(#coreFilter)"
        />
        <circle
          cx="174"
          cy="186"
          r="3.5"
          fill="#FFFFFF"
          filter="url(#coreFilter)"
        />
      </g>

      {/* ── SLIDER 03: 3D DATA RESILIENCE (VIBRANT CYBER SKY-BLUE) ───────────── */}
      <g opacity={weights[2]} className="transition-opacity duration-75">
        {/* Tier 3: Bottom Disc Cylinder */}
        <ellipse cx="170" cy="170" rx="27" ry="8.8" fill="url(#db3DTopGrad)" stroke="#BAE6FD" strokeWidth="1" />
        <path d="M 143,170 A 27 8.8 0 0 0 197,170 L 197,177 A 27 8.8 0 0 1 143,177 Z" fill="url(#db3DSideGrad)" />
        <circle cx="185" cy="173" r="2.2" fill="#E0F2FE" filter="url(#coreFilter)" />
        <circle cx="185" cy="173" r="1.2" fill="#FFFFFF" />

        {/* Tier 2: Middle Disc Cylinder */}
        <ellipse cx="170" cy="154" rx="27" ry="8.8" fill="url(#db3DTopGrad)" stroke="#BAE6FD" strokeWidth="1.2" />
        <path d="M 143,154 A 27 8.8 0 0 0 197,154 L 197,161 A 27 8.8 0 0 1 143,161 Z" fill="url(#db3DSideGrad)" />
        <circle cx="185" cy="157" r="2.2" fill="#E0F2FE" filter="url(#coreFilter)" />
        <circle cx="185" cy="157" r="1.2" fill="#FFFFFF" />

        {/* Tier 1: Top Disc Cylinder */}
        <ellipse cx="170" cy="138" rx="27" ry="8.8" fill="url(#db3DTopGrad)" stroke="#FFFFFF" strokeWidth="1.6" />
        <path d="M 143,138 A 27 8.8 0 0 0 197,138 L 197,145 A 27 8.8 0 0 1 143,145 Z" fill="url(#db3DSideGrad)" />
        <circle cx="185" cy="141" r="2.2" fill="#E0F2FE" filter="url(#coreFilter)" />
        <circle cx="185" cy="141" r="1.2" fill="#FFFFFF" />

        {/* Specular White Glint on Top Disc */}
        <ellipse cx="170" cy="138" rx="20" ry="5.5" fill="#FFFFFF" opacity="0.65" />
      </g>

      {/* ── SLIDER 04: 3D GOVERNED FLEET (SOVEREIGN EMERALD GREEN) ────────────── */}
      <g opacity={weights[3]} className="transition-opacity duration-75">
        {/* Shackle Arch (Chrome Emerald Highlight) */}
        <path
          d="M 157,144 L 157,130 A 13 13 0 0 1 183,130 L 183,144"
          fill="none"
          stroke="#A7F3D0"
          strokeWidth="4.5"
          strokeLinecap="round"
          filter="url(#coreFilter)"
        />
        <path
          d="M 157,144 L 157,130 A 13 13 0 0 1 183,130 L 183,144"
          fill="none"
          stroke="#FFFFFF"
          strokeWidth="2.4"
          strokeLinecap="round"
        />

        {/* Rounded Rectangular Padlock Body */}
        <rect
          x="146"
          y="142"
          width="48"
          height="38"
          rx="9"
          fill="url(#lock3DGrad)"
          stroke="#6EE7B7"
          strokeWidth="1.8"
          filter="url(#coreFilter)"
        />
        <rect
          x="148"
          y="144"
          width="44"
          height="34"
          rx="7"
          fill="#065F46"
          opacity="0.88"
        />
        <rect
          x="148"
          y="144"
          width="22"
          height="34"
          rx="7"
          fill="#059669"
        />

        {/* Glowing Keyhole (White + Emerald Halo) */}
        <circle
          cx="170"
          cy="157"
          r="4.2"
          fill="#FFFFFF"
        />
        <polygon
          points="168,157 166,168 174,168 172,157"
          fill="#FFFFFF"
        />
        <circle
          cx="170"
          cy="161"
          r="6.5"
          fill="none"
          stroke="#34D399"
          strokeWidth="1.4"
          opacity="0.85"
        />
      </g>

      {/* ── 6. 3D TRANSLUCENT GLASS SPHERE OVERLAY & SPECULAR GLOSS ─────────── */}
      {/* Glass Surface Shading */}
      <circle
        cx="170"
        cy="154"
        r="54"
        fill="url(#glassSphereGrad)"
      />
      {/* Outer Specular Rim */}
      <circle
        cx="170"
        cy="154"
        r="54"
        fill="none"
        stroke="rgba(255, 255, 255, 0.85)"
        strokeWidth="1.6"
        filter="url(#coreFilter)"
      />

      {/* Top-Left Curved Crescent Glass Gloss Highlight */}
      <path
        d="M 136,128 Q 148,110 172,110 Q 186,110 196,118 Q 175,116 156,128 Q 142,138 138,150 Q 133,137 136,128 Z"
        fill="#FFFFFF"
        opacity="0.82"
      />

      {/* Secondary Bottom Specular Rim Reflection */}
      <ellipse
        cx="170"
        cy="196"
        rx="34"
        ry="7"
        fill="#FFFFFF"
        opacity="0.4"
      />

      {/* Pinpoint Specular Light Glint */}
      <circle
        cx="146"
        cy="122"
        r="2.8"
        fill="#FFFFFF"
        opacity="0.95"
      />

      {/* ── 7. TILTED PLANETARY ORBITAL RING (FRONT HALF - IN FRONT OF SPHERE) ─ */}
      <g transform="translate(170, 154) rotate(-18)">
        <path
          d="M -76,0 A 76 22 0 0 0 76,0"
          stroke={blendedColor}
          strokeWidth="1.8"
          fill="none"
          filter="url(#coreFilter)"
        />
        {/* Orbiting Satellite Bead (When in front of sphere) */}
        {isBeadFront && (
          <g transform={`translate(${beadX0}, ${beadY0})`}>
            <circle r="4.5" fill={blendedColor} filter="url(#coreFilter)" />
            <circle r="2.2" fill="#FFFFFF" />
          </g>
        )}
      </g>
    </svg>
  );
};

export const FourEnginesOrbit = () => {
  // Continuous global progress in [0, 4)
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const animFrameRef = useRef(null);
  const lastTimeRef = useRef(null);

  // ── CONTINUOUS UNIFIED TIME CLOCK ──────────────────────────────────────────
  useEffect(() => {
    if (isPaused) {
      lastTimeRef.current = null;
      return;
    }

    const tick = (now) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = now;
      }
      const delta = now - lastTimeRef.current;
      lastTimeRef.current = now;

      // 4 steps complete over 16,000ms (4s per step)
      setProgress((prev) => (prev + (delta / STEP_DURATION_MS)) % 4);

      animFrameRef.current = requestAnimationFrame(tick);
    };

    animFrameRef.current = requestAnimationFrame(tick);
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [isPaused]);

  // Derived continuous metrics:
  const weights = [0, 1, 2, 3].map((k) => getStepWeight(progress, k));
  const blendedColor = getBlendedColor(weights);

  // Primary active step for discrete items (counter, telemetry expansion, mobile)
  const activeIdx = Math.floor((progress + 0.25) % 4);
  const current = ENGINES[activeIdx];

  // Head and tail angles on ellipse:
  // Node 01: -135° (top-left)
  // Node 02: -45° (top-right)
  // Node 03: +45° (bottom-right)
  // Node 04: +135° (bottom-left)
  const headDeg = -135 + progress * 90;
  const tailDeg = headDeg - 45;

  const headPt = getEllipsePt(headDeg);
  const tailPt = getEllipsePt(tailDeg);

  // SVG elliptical arc for the traveling glowing beam
  const arcD = `M ${tailPt.x.toFixed(1)},${tailPt.y.toFixed(1)} A ${ELLIPSE_RX} ${ELLIPSE_RY} 0 0 1 ${headPt.x.toFixed(1)},${headPt.y.toFixed(1)}`;

  // SVG fan for the radar sweeper beam
  const fanD = `M ${ELLIPSE_CX},${ELLIPSE_CY} L ${tailPt.x.toFixed(1)},${tailPt.y.toFixed(1)} A ${ELLIPSE_RX} ${ELLIPSE_RY} 0 0 1 ${headPt.x.toFixed(1)},${headPt.y.toFixed(1)} Z`;

  // 8 ambient radar nodes surrounding center
  const radarCircleRadius = 185;
  const ambientRadarNodes = [0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
    const rad = (deg * Math.PI) / 180;
    return {
      x: ELLIPSE_CX + radarCircleRadius * Math.cos(rad),
      y: ELLIPSE_CY + radarCircleRadius * Math.sin(rad)
    };
  });

  // User interactions:
  const handleCardHover = (idx) => {
    setIsPaused(true);
    setProgress(idx);
  };

  const handleCardLeave = () => {
    setIsPaused(false);
  };

  const handleSelect = (idx, e) => {
    e?.stopPropagation();
    setProgress(idx);
  };

  const handlePrev = (e) => {
    e?.stopPropagation();
    setProgress((prev) => (Math.floor(prev) - 1 + 4) % 4);
  };

  const handleNext = (e) => {
    e?.stopPropagation();
    setProgress((prev) => (Math.floor(prev) + 1) % 4);
  };

  return (
    <section
      id="capabilities"
      className="py-6 sm:py-8 lg:py-10 px-4 sm:px-6 bg-white border-b border-black/[0.06] relative overflow-hidden select-none"
    >
      <div className="max-w-7xl mx-auto">
        
        {/* ── COMPACT HEADER ROW ─────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4 sm:mb-5">
          <div className="text-left">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#FFF6F0] border border-[#FDE5D6] text-[10px] sm:text-[11px] font-mono font-bold text-[#FF5A1F] uppercase mb-1.5 shadow-2xs">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A1F] animate-pulse" />
              <span>FOUR ENGINES</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-display font-extrabold text-[#0B0B0B] leading-tight tracking-tight">
              One Autonomous <span className="text-[#FF5A1F]">Defense</span> Layer.
            </h2>
            <p className="text-xs sm:text-sm text-neutral-600 font-normal mt-1 max-w-xl line-clamp-1 sm:line-clamp-none">
              AI-powered engines working together to protect, manage, and restore your infrastructure.
            </p>
          </div>

          <div className="shrink-0 pb-0.5">
            <button
              onClick={() => openConsultationModal("Guardian Four Engines Architecture")}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white hover:bg-neutral-50 text-neutral-800 text-xs font-semibold border border-neutral-200/90 shadow-xs hover:border-neutral-300 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
            >
              <span>View Architecture</span>
              <ArrowRight className="w-3.5 h-3.5 text-[#FF5A1F]" />
            </button>
          </div>
        </div>

        {/* ── MAIN ANIMATED 3D ISOMETRIC STAGE ─────────────────────────────────── */}
        <div
          className="relative w-full rounded-[28px] sm:rounded-[32px] bg-gradient-to-b from-[#FBFBFE] via-[#FFFFFF] to-[#F8F9FD] border border-neutral-200/80 shadow-lg overflow-hidden p-3 sm:p-5 lg:p-6 h-[480px] sm:h-[510px] lg:h-[530px] flex flex-col justify-between"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={handleCardLeave}
        >
          {/* Subtle Ambient Lighting Glow */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[650px] h-[400px] rounded-full blur-3xl pointer-events-none transition-colors duration-700"
            style={{
              backgroundColor: blendedColor,
              opacity: 0.14
            }}
          />

          {/* Isometric grid dots */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.03]"
            style={{
              backgroundImage: "radial-gradient(#000000 1px, transparent 1px)",
              backgroundSize: "24px 24px"
            }}
          />

          {/* ── 1. SVG ORBITAL RADAR CONCENTRIC RINGS & TRAVELING GLOWING BEAM ─ */}
          <svg
            viewBox="0 0 1000 500"
            className="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              {/* Dynamic traveling beam gradient */}
              <linearGradient
                id="beamTravelingGrad"
                gradientUnits="userSpaceOnUse"
                x1={tailPt.x}
                y1={tailPt.y}
                x2={headPt.x}
                y2={headPt.y}
              >
                <stop offset="0%" stopColor={blendedColor} stopOpacity="0" />
                <stop offset="35%" stopColor={blendedColor} stopOpacity="0.45" />
                <stop offset="85%" stopColor={blendedColor} stopOpacity="1" />
                <stop offset="100%" stopColor="#FFFFFF" stopOpacity="1" />
              </linearGradient>

              {/* Radar fan gradient */}
              <radialGradient
                id="radarFanGrad"
                cx={ELLIPSE_CX}
                cy={ELLIPSE_CY}
                r={ELLIPSE_RX}
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0%" stopColor={blendedColor} stopOpacity="0.22" />
                <stop offset="55%" stopColor={blendedColor} stopOpacity="0.08" />
                <stop offset="100%" stopColor={blendedColor} stopOpacity="0" />
              </radialGradient>

              <filter id="photonGlow" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Circular Concentric Radar Rings */}
            <circle
              cx={ELLIPSE_CX}
              cy={ELLIPSE_CY}
              r={radarCircleRadius}
              fill="none"
              stroke="#FDBA74"
              strokeWidth="1.2"
              strokeDasharray="6 8"
              opacity="0.35"
            />
            <circle
              cx={ELLIPSE_CX}
              cy={ELLIPSE_CY}
              r="135"
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="1"
              strokeDasharray="3 5"
              opacity="0.5"
            />
            <circle
              cx={ELLIPSE_CX}
              cy={ELLIPSE_CY}
              r="85"
              fill="none"
              stroke="#FED7AA"
              strokeWidth="1"
              opacity="0.35"
            />

            {/* 8 Ambient Glowing Radar Nodes on the Circle */}
            {ambientRadarNodes.map((node, i) => (
              <g key={`amb-node-${i}`} transform={`translate(${node.x}, ${node.y})`}>
                <circle r="3.2" fill="#FF7A3D" opacity="0.6" filter="url(#photonGlow)" />
                <circle r="1.5" fill="#FFFFFF" opacity="0.9" />
              </g>
            ))}

            {/* Symmetrical Outer Concentric Ellipse Rings for 4 Engines */}
            <ellipse
              cx={ELLIPSE_CX}
              cy={ELLIPSE_CY}
              rx={ELLIPSE_RX}
              ry={ELLIPSE_RY}
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="1.2"
              strokeDasharray="4 6"
              className="opacity-70"
            />
            <ellipse
              cx={ELLIPSE_CX}
              cy={ELLIPSE_CY}
              rx="230"
              ry="92"
              fill="none"
              stroke="#E2E8F0"
              strokeWidth="1"
              className="opacity-50"
            />

            {/* ── RADAR SWEEPER FAN ─────────────────────────────────────────── */}
            <path
              d={fanD}
              fill="url(#radarFanGrad)"
            />

            {/* ── CENTER RADAR SCANNER LINE TO HEAD ───────────────────────────── */}
            <line
              x1={ELLIPSE_CX}
              y1={ELLIPSE_CY}
              x2={headPt.x}
              y2={headPt.y}
              stroke={blendedColor}
              strokeWidth="1.6"
              strokeOpacity="0.8"
            />

            {/* ── GLOWING TRAVELING BEAM ALONG ELLIPSE (HEAD TO TAIL) ─────────── */}
            <path
              d={arcD}
              fill="none"
              stroke={blendedColor}
              strokeWidth="9"
              strokeLinecap="round"
              strokeOpacity="0.32"
              filter="url(#photonGlow)"
            />
            <path
              d={arcD}
              fill="none"
              stroke="url(#beamTravelingGrad)"
              strokeWidth="3.5"
              strokeLinecap="round"
              filter="url(#photonGlow)"
            />

            {/* Leading White Photon Orb at the Head of the Beam */}
            <circle
              cx={headPt.x}
              cy={headPt.y}
              r="4.5"
              fill="#FFFFFF"
              filter="url(#photonGlow)"
            />
            <circle
              cx={headPt.x}
              cy={headPt.y}
              r="2.2"
              fill="#FFFFFF"
            />

            {/* ── 4 DIAMOND STEP MARKERS ON THE ORBIT PATH ─────────────────────── */}
            {ENGINES.map((eng, idx) => {
              const w = weights[idx];
              const isHighlight = w > 0.05;
              return (
                <g key={`node-${eng.id}`} transform={`translate(${eng.node.x}, ${eng.node.y})`}>
                  <rect
                    x="-6"
                    y="-6"
                    width="12"
                    height="12"
                    transform="rotate(45)"
                    fill={isHighlight ? eng.color : "#CBD5E1"}
                    filter={isHighlight ? "url(#photonGlow)" : undefined}
                    opacity={0.6 + 0.4 * w}
                  />
                  {w > 0.4 && (
                    <circle
                      cx="0"
                      cy="0"
                      r="14"
                      fill="none"
                      stroke={eng.color}
                      strokeWidth="1.5"
                      opacity={w}
                      className="animate-ping"
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* ── 2. CENTRAL 3D PEDESTAL & 3D SPHERE HOLOGRAPHIC CORE ─────────── */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center justify-center pointer-events-none -mt-3">
            <div className="relative w-[230px] sm:w-[260px] aspect-[340/300] flex items-center justify-center">
              <Engine3DIsometricCore
                weights={weights}
                blendedColor={blendedColor}
                progress={progress}
              />
            </div>
          </div>

          {/* ── 3. THE 4 COMPACT INTERACTIVE ENGINE CARDS (Desktop Quadrants) ──── */}
          <div className="hidden lg:block absolute inset-0 pointer-events-none">
            {ENGINES.map((eng, idx) => {
              const w = weights[idx];
              const isPrimary = activeIdx === idx;
              const IconComponent = eng.icon;
              const drawerOpacity = Math.max(0, (w - 0.25) / 0.75);

              return (
                <div
                  key={eng.id}
                  onClick={(e) => handleSelect(idx, e)}
                  onMouseEnter={() => handleCardHover(idx)}
                  onMouseLeave={handleCardLeave}
                  style={{
                    transform: `scale(${0.97 + 0.05 * w})`,
                    opacity: 0.68 + 0.32 * w,
                    borderColor: w > 0.08 ? eng.color : "rgba(229, 231, 235, 0.8)",
                    borderWidth: w > 0.25 ? "2px" : "1px",
                    boxShadow: w > 0.1
                      ? `0 14px 34px -6px ${eng.color}${Math.round(w * 55).toString(16).padStart(2, "0")}`
                      : "0 2px 8px rgba(0,0,0,0.03)",
                    backgroundColor: w > 0.3 ? "rgba(255, 255, 255, 0.98)" : "rgba(255, 255, 255, 0.85)"
                  }}
                  className={`absolute ${eng.cardClass} ${
                    isPrimary ? "w-[260px] xl:w-[280px]" : "w-[235px] xl:w-[250px]"
                  } p-3 sm:p-3.5 rounded-2xl cursor-pointer pointer-events-auto transition-all duration-150 backdrop-blur-xl text-left z-20`}
                >
                  <div className="flex items-center justify-between gap-2.5">
                    {/* Left: Icon Badge (Always in distinct engine theme color) */}
                    <div
                      className="w-10 h-10 rounded-xl border flex items-center justify-center shrink-0 transition-all duration-300"
                      style={{
                        backgroundColor: `${eng.color}15`,
                        borderColor: w > 0.1 ? `${eng.color}60` : `${eng.color}30`,
                        color: eng.color,
                        transform: `scale(${1 + 0.08 * w})`,
                        boxShadow: w > 0.2 ? `0 4px 16px -2px ${eng.color}40` : "none"
                      }}
                    >
                      <IconComponent className="w-4 h-4" />
                    </div>

                    {/* Middle: Number, Title, Subtitle */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span
                          className="text-[10px] font-mono font-bold transition-colors"
                          style={{ color: eng.color }}
                        >
                          {eng.num}
                        </span>
                        {w > 0.3 && (
                          <span
                            className="w-1.5 h-1.5 rounded-full animate-ping"
                            style={{ backgroundColor: eng.color, opacity: w }}
                          />
                        )}
                      </div>
                      <h4 className="text-xs sm:text-sm font-bold text-[#0B0B0B] truncate leading-tight">
                        {eng.title}
                      </h4>
                      <p className="text-[10px] text-neutral-500 truncate mt-0.5 font-medium">
                        {eng.sub}
                      </p>
                    </div>

                    {/* Right: Circular Arrow Button */}
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-200"
                      style={{
                        backgroundColor: w > 0.2 ? eng.color : "#F3F4F6",
                        color: w > 0.2 ? "#FFFFFF" : "#9CA3AF",
                        boxShadow: w > 0.2 ? `0 4px 14px -2px ${eng.glow}` : "none"
                      }}
                    >
                      <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>

                  {/* ACTIVE CARD FOOTER: Inline Live Telemetry Snippet */}
                  {drawerOpacity > 0 && (
                    <div
                      style={{ opacity: drawerOpacity }}
                      className="pt-2.5 mt-2.5 border-t border-neutral-100 flex items-center justify-between text-[10px] font-mono transition-opacity duration-150"
                    >
                      <span className="text-neutral-500 font-semibold">{eng.telemetry.stat1}</span>
                      <span className="text-neutral-400 font-normal">·</span>
                      <span className="text-neutral-500 font-semibold">{eng.telemetry.stat2}</span>
                      <span className="text-neutral-400 font-normal">·</span>
                      <span className="font-bold" style={{ color: eng.color }}>{eng.telemetry.stat3}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── 4. MOBILE / TABLET VIEW (< 1024px) ────────────────────────────── */}
          <div className="lg:hidden relative z-20 w-full max-w-sm mx-auto pt-32 pb-2">
            <div
              className="bg-white/95 backdrop-blur-xl border-2 rounded-2xl p-4 text-left shadow-lg transition-all duration-300"
              style={{
                borderColor: current.color,
                boxShadow: `0 12px 30px -6px ${current.glow}`
              }}
            >
              {(() => {
                const CurrentIcon = current.icon;
                return (
                  <div>
                    <div className="flex items-center justify-between gap-2.5 mb-2">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border"
                        style={{
                          backgroundColor: `${current.color}15`,
                          borderColor: `${current.color}40`,
                          color: current.color
                        }}
                      >
                        <CurrentIcon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-[10px] font-mono font-bold" style={{ color: current.color }}>
                          {current.num}
                        </span>
                        <h4 className="text-sm font-bold text-[#0B0B0B] leading-tight">
                          {current.title}
                        </h4>
                        <p className="text-[10px] text-neutral-500 font-medium">
                          {current.sub}
                        </p>
                      </div>
                      <div
                        className="w-8 h-8 rounded-full text-white flex items-center justify-center shrink-0 shadow-xs"
                        style={{ backgroundColor: current.color }}
                      >
                        <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between text-[10px] font-mono pt-2 border-t border-neutral-100 text-neutral-600">
                      <span>{current.telemetry.stat1}</span>
                      <span>·</span>
                      <span>{current.telemetry.stat2}</span>
                      <span>·</span>
                      <span className="font-bold" style={{ color: current.color }}>{current.telemetry.stat3}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Mobile 4-Button Step Selector */}
            <div className="grid grid-cols-4 gap-1.5 mt-3">
              {ENGINES.map((e, idx) => (
                <button
                  key={e.id}
                  onClick={(ev) => handleSelect(idx, ev)}
                  className="py-1.5 px-1 rounded-lg text-center font-mono text-[10px] font-bold transition-all"
                  style={{
                    backgroundColor: activeIdx === idx ? e.color : "#FFFFFF",
                    color: activeIdx === idx ? "#FFFFFF" : "#6B7280",
                    border: activeIdx === idx ? "none" : "1px solid #E5E7EB"
                  }}
                >
                  {e.num}
                </button>
              ))}
            </div>
          </div>

          {/* ── 5. COMPACT BOTTOM CONTROLS (Counter, Segmented Bars, Arrows) ─── */}
          <div className="pt-3 mt-auto border-t border-neutral-200/80 flex items-center justify-between gap-3 relative z-30">
            {/* Bottom-Left: Counter (01 / 04) + 4 Segmented Interactive Progress Bars */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="font-mono text-xs sm:text-sm tracking-wider shrink-0">
                <span className="font-bold" style={{ color: current.color }}>
                  0{activeIdx + 1}
                </span>
                <span className="text-neutral-400 font-medium ml-1">/ 04</span>
              </div>

              {/* 4 Segmented Progress Bars (Synchronized to continuous progress) */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                {ENGINES.map((eng, idx) => {
                  let fillPct = 0;
                  if (progress >= idx + 1) {
                    fillPct = 100;
                  } else if (progress >= idx) {
                    fillPct = Math.min(100, Math.max(0, (progress - idx) * 100));
                  } else {
                    fillPct = 0;
                  }

                  return (
                    <button
                      key={idx}
                      onClick={(e) => handleSelect(idx, e)}
                      onMouseEnter={() => handleCardHover(idx)}
                      onMouseLeave={handleCardLeave}
                      className="w-8 sm:w-12 h-1.5 rounded-full bg-neutral-200/90 overflow-hidden relative transition-colors cursor-pointer group"
                      aria-label={`Go to engine 0${idx + 1}`}
                      title={eng.title}
                    >
                      <div
                        className="h-full"
                        style={{
                          backgroundColor: eng.color,
                          width: `${fillPct}%`
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Bottom-Right: Arrow Buttons (← and →) */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrev}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-neutral-200/90 bg-white hover:bg-neutral-50 text-neutral-600 flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                title="Previous Engine Slide"
                aria-label="Previous Slide"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={handleNext}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full border bg-white flex items-center justify-center transition-all hover:scale-105 active:scale-95 cursor-pointer shadow-2xs"
                style={{
                  borderColor: `${current.color}66`,
                  color: current.color,
                  boxShadow: `0 2px 12px ${current.glow}`
                }}
                title="Next Engine Slide"
                aria-label="Next Slide"
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
